// adminAiEndpointAllowlist — S-012: gate custom AI-chat-endpoints mot
// en whitelist innan hela konversationen POSTas dit. Fönstret är:
// admin-user sätter `endpoint` i localStorage via en XSS/extension-
// komprometterad kanal → hela chat-historiken + bug-/feature-report-
// context skickas till attacker.example.
//
// Strikt default-allowlist: OpenAI, Anthropic, Lovable-gateway, + own
// Supabase-host. Callers kan pass:a extra allowed-URLs om admin
// medvetet kör mot annan provider (sedan ska de också rutinmässigt
// förvarnas via UI).
//
// Pure: inga side-effekter. URL-parse via `new URL()` så ogiltiga
// strings → isAllowedEndpoint returnerar false.

export const DEFAULT_ALLOWLIST: readonly string[] = [
  'api.openai.com',
  'api.anthropic.com',
  'ai.gateway.lovable.dev',
];

export interface AllowlistOptions {
  /** Extra origins som user medvetet godkänt (t.ex. egen proxy). */
  extraAllowed?: readonly string[];
  /** True → tillåt `localhost:*` + `127.0.0.1:*` för dev. Default false. */
  allowLocalhost?: boolean;
  /** Egen Supabase-projekt-host (URL utan path). När satt tillåts
   * `<host>/functions/v1/*` som default. */
  supabaseHost?: string;
}

/** Returnerar true om `url` parsear till en hostname som är tillåten. */
export function isAllowedEndpoint(
  url: string | null | undefined,
  opts: AllowlistOptions = {},
): boolean {
  if (!url) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  // Default allowlist
  if (DEFAULT_ALLOWLIST.includes(host)) return true;
  // Supabase-host (egen backend)
  if (opts.supabaseHost) {
    try {
      const supaHost = new URL(opts.supabaseHost).hostname.toLowerCase();
      if (host === supaHost) return true;
    } catch { /* ignore */ }
  }
  // Extra user-approved
  if (opts.extraAllowed?.some((allowed) => {
    try {
      return new URL(allowed).hostname.toLowerCase() === host;
    } catch {
      return allowed.toLowerCase() === host;
    }
  })) {
    return true;
  }
  // Localhost (dev-läge)
  if (opts.allowLocalhost) {
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  }
  return false;
}

/** Returnerar human-readable varnings-text för icke-tillåten endpoint. */
export function explainRejection(url: string | null | undefined): string {
  if (!url) return 'Ingen endpoint konfigurerad.';
  try {
    const host = new URL(url).hostname;
    return `Endpoint "${host}" är inte i allowlist — AI-chatten vägrar skicka data dit. Kontrollera localStorage-configen eller uppdatera allowlist.`;
  } catch {
    return `Ogiltig endpoint-URL "${url}".`;
  }
}
